import React from 'react';

interface LogoProps {
  size?: number;
  showText?: boolean;
  variant?: 'default' | 'dark' | 'minimal';
  className?: string;
}

export const Logo: React.FC<LogoProps> = ({
  size = 40,
  showText = true,
  variant = 'default',
  className = ''
}) => {
  const getGradientId = () => `grad-${variant}-${size}`;
  const getAccentGradientId = () => `grad-accent-${variant}-${size}`;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0"
      >
        <defs>
          <linearGradient id={getGradientId()} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: variant === 'dark' ? '#ffffff' : '#667eea', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: variant === 'dark' ? '#ffffff' : '#764ba2', stopOpacity: 1 }} />
          </linearGradient>
          <linearGradient id={getAccentGradientId()} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: '#f093fb', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#f5576c', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <g transform="translate(50, 50)">
          {/* Eye shape */}
          <ellipse
            cx="0"
            cy="0"
            rx="40"
            ry="25"
            fill="none"
            stroke={variant === 'dark' ? 'white' : `url(#${getGradientId()})`}
            strokeWidth={size < 48 ? "4" : "3.5"}
            strokeLinecap="round"
            transform="rotate(-5)"
          />

          {/* Growth line */}
          <polyline
            points="-26,9 -16,5 -6,2 4,-5 14,-9 26,-16"
            fill="none"
            stroke={variant === 'dark' ? '#f093fb' : `url(#${getAccentGradientId()})`}
            strokeWidth={size < 48 ? "3" : "2.5"}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points - only show if size is large enough */}
          {size >= 64 && variant !== 'minimal' && (
            <g>
              <circle cx="-26" cy="9" r="1.8" fill={`url(#${getAccentGradientId()})`}/>
              <circle cx="-6" cy="2" r="1.8" fill={`url(#${getAccentGradientId()})`}/>
              <circle cx="14" cy="-9" r="1.8" fill={`url(#${getAccentGradientId()})`}/>
              <circle cx="26" cy="-16" r="1.8" fill={`url(#${getGradientId()})`}/>
            </g>
          )}
        </g>
      </svg>

      {showText && (
        <span
          className={`font-light tracking-tight ${
            size >= 64 ? 'text-4xl' :
            size >= 48 ? 'text-2xl' :
            'text-xl'
          }`}
          style={{
            background: variant === 'dark'
              ? 'white'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          ease
        </span>
      )}
    </div>
  );
};

export default Logo;